import { computeRfiExportOptions, getUserType } from "./helperMethods";

/**
 * VQQ cancel / reactivate rules — adjust arrays here to change role or status gates.
 */
/** Complete, Released and Published are terminal for cancel; only a still-pending request may be cancelled. */
export const RFI_NON_CANCELLABLE_STATUSES = ["Complete", "Released", "Published"];

/** Roles allowed to cancel/reactivate; extend or remove entries to tighten permissions. */
export const RFI_REQUEST_ACTION_ROLES = ["sponsor", "vendor", "admin"];

export function normalizeRfiStatus(status) {
  return String(status ?? "").trim();
}

/** True when any list/detail field indicates the request is archived (Archive vs Unarchive menu). */
export function isRequestArchivedStatus(status, details) {
  const candidates = [];
  const push = (value) => {
    const normalized = normalizeRfiStatus(value);
    if (normalized) candidates.push(normalized);
  };

  push(status);
  if (details) {
    push(details.request_status);
    push(details.requestStatus);
    push(details.status);
    push(details.Status);
    push(details.document_status);
    push(details.documentStatus);
  }

  return candidates.some((s) => s.toLowerCase() === "archived");
}

/** Status string for RFI/VQQ list rows (admin get_rfis may use `Status` or `status`). */
export function resolveRfiListRowStatus(item) {
  if (!item) return "";
  const raw = normalizeRfiStatus(
    item.status ??
      item.request_status ??
      item.requestStatus ??
      item.Status
  );
  // Legacy DB rows before migration may still say "Not Sent"; display as Sent.
  return raw.toLowerCase() === "not sent" ? "Sent" : raw;
}

/** List/grid rows may expose status on different keys depending on API DTO casing. */
export function resolveRfiActionStatus(status, details) {
  if (isRequestArchivedStatus(status, details)) {
    return "Archived";
  }

  if (details) {
    const cancelledOn = details.cancelled_on ?? details.cancelledOn;
    if (cancelledOn) {
      return "Cancelled";
    }
  }

  const rowStatus = normalizeRfiStatus(
    status ??
      details?.request_status ??
      details?.requestStatus ??
      details?.status ??
      details?.Status
  );

  if (rowStatus && isRfiCancelledStatus(rowStatus, details)) {
    return rowStatus;
  }

  if (details) {
    const docStatus =
      details.document_status ??
      details.documentStatus ??
      details.request_status;
    if (docStatus && isRfiCancelledStatus(docStatus)) {
      return normalizeRfiStatus(docStatus);
    }
  }

  const resolved = normalizeRfiStatus(
    rowStatus ||
      details?.request_status ||
      details?.document_status ||
      details?.documentStatus
  );

  // Legacy rows before data migration; align with resolveRfiListRowStatus display.
  if (resolved.toLowerCase() === "not sent") {
    return "Sent";
  }

  return resolved;
}

/**
 * Status that should drive action decisions (export options, cancel). Archiving must be
 * transparent to the action menu, so when a request is archived we resolve the status it
 * held before archiving (status_before_archive), falling back to the normal resolved
 * status when no pre-archive status is available (e.g. legacy rows archived before the
 * field existed). Identical to resolveRfiActionStatus for non-archived requests.
 */
export function resolveRfiFunctionalStatus(status, details) {
  if (isRequestArchivedStatus(status, details)) {
    const priorStatus = normalizeRfiStatus(
      details?.status_before_archive ??
        details?.statusBeforeArchive ??
        details?.StatusBeforeArchive
    );
    if (priorStatus) {
      return resolveRfiActionStatus(priorStatus, null);
    }
  }
  return resolveRfiActionStatus(status, details);
}

export function isRfiCancelledStatus(status, details) {
  if (details) {
    const cancelledOn = details.cancelled_on ?? details.cancelledOn;
    if (cancelledOn) {
      return true;
    }
  }

  const s = normalizeRfiStatus(status).toLowerCase();
  return s === "cancelled" || s === "canceled";
}

export function canCancelRfiByStatus(status, details) {
  const s = resolveRfiActionStatus(status, details);
  if (!s) return false;
  if (isRfiCancelledStatus(s, details)) return false;
  return !RFI_NON_CANCELLABLE_STATUSES.some(
    (blocked) => blocked.toLowerCase() === s.toLowerCase()
  );
}

export function canCancelRfiRequest(userRole) {
  const userType = getUserType(userRole);
  return RFI_REQUEST_ACTION_ROLES.includes(userType);
}

/** Vendors are excluded: resend on a Sent request re-invites the vendor, so only the requesting side may trigger it. */
export const RFI_RESEND_INVITE_ROLES = ["sponsor", "admin"];

export function canResendRfiInvite(userRole) {
  return RFI_RESEND_INVITE_ROLES.includes(getUserType(userRole));
}

/** Vendors are excluded: they hold the documents, so requesting access to one is a requesting-side action. */
export const RFI_REQUEST_ACCESS_ROLES = ["sponsor", "admin"];

export function canRequestLibraryDocAccess(userRole) {
  return RFI_REQUEST_ACCESS_ROLES.includes(getUserType(userRole));
}

export function canReactivateCancelledRfi(status, details) {
  const resolved = resolveRfiActionStatus(status, details);
  return isRfiCancelledStatus(resolved, details);
}

export function canReactivateRfiRequest(userRole) {
  return canCancelRfiRequest(userRole);
}

/** List/grid rows may expose acceptance on different keys depending on API DTO casing. */
export function resolveRfiQuestionnaireAccepted(details) {
  if (!details) return false;
  if (details.cancelled_locally === true || details.cancelledLocally === true) {
    return true;
  }
  const accepted =
    details.questionnaire_accepted ??
    details.questionnaireAccepted ??
    details.QuestionnaireAccepted;
  if (accepted === true) return true;
  // Set together when the vendor accepts in HyperComply (even if questionnaire_accepted is omitted from list DTO).
  const pvqId =
    details.purchase_vendor_questionnaire_id ??
    details.purchaseVendorQuestionnaireId ??
    details.PurchaseVendorQuestionnaireId;
  return pvqId != null && pvqId !== "";
}

/**
 * Reactivate/re-open menu label. Vendors re-open their own cancelled request ("Re-open
 * Request"); the sponsor/admin side resends the HyperComply invite pre-accept ("Resend
 * Invite") or reactivates locally post-accept ("Reactivate Request"). Same endpoint either way.
 */
export function reactivateCancelledRfiLabel(details, userRole) {
  if (getUserType(userRole) === "vendor") {
    return "Re-open Request";
  }
  return resolveRfiQuestionnaireAccepted(details)
    ? "Reactivate Request"
    : "Resend Invite";
}

/** Responses tab hidden for cancelled VQQs (pre- and post-accepted behave the same). */
export function canShowRfiResponsesTab(userRole, status, questionnaireAccepted, details) {
  const s = resolveRfiActionStatus(status, details);
  const isVendor = userRole === "Provider User";

  if (isRfiCancelledStatus(s, details)) {
    return false;
  }

  if (isVendor) {
    return questionnaireAccepted === true;
  }

  return s !== "Sent" && s !== "In Progress";
}

/**
 * The single source of truth for the VQQ Details export menu: which options
 * to offer and whether the menu renders at all. Combines the risk-augmented
 * visibility gate (Complete + scored + published for sponsors and vendors;
 * Diligent admins may render pre-publish), archive-transparent status
 * resolution, and the acceptance gate on partial exports (a Sent or re-opened
 * VQQ has no HyperComply copy to pull yet).
 */
export function computeRfiDetailsExportState({
  status,
  details,
  userRole,
  isDiligentAdmin = false,
  canSeeRisk = false,
  scoredSnapshot = null,
}) {
  const isRfiComplete = status === "Complete";
  const isDiligentTemplate =
    (!!scoredSnapshot?.mode && scoredSnapshot.mode !== "None") ||
    (isDiligentAdmin && !!scoredSnapshot?.scoring_run_id);
  const isRiskScoringRequested = isDiligentTemplate;
  const hasPublishedSnapshot = scoredSnapshot?.is_published === true;
  const viewerType = getUserType(userRole);
  const isSponsorViewer = viewerType === "sponsor";
  const baseConditions =
    canSeeRisk && isRfiComplete && isDiligentTemplate && isRiskScoringRequested;
  // Risk-augmented exports are a sponsor-and-admin surface: sponsors get them
  // once a snapshot is published, admins may render pre-publish, and vendors
  // never see them regardless of their org's entitlement.
  const publishGateSatisfied = isDiligentAdmin
    ? true
    : isSponsorViewer && hasPublishedSnapshot;
  const showRiskAugmented = baseConditions && publishGateSatisfied;

  const options = computeRfiExportOptions(
    resolveRfiFunctionalStatus(status, details),
    userRole,
    { showRiskAugmented }
  );
  const requiresAcceptance = options?.includes("EXCEL_PARTIAL");
  const canExport = Boolean(
    options &&
      (!requiresAcceptance || resolveRfiQuestionnaireAccepted(details))
  );
  return { options, canExport, showRiskAugmented };
}
